import { EXPERIENCE_FOLLOW_UP_STEPS } from "@repo/contract/training";
import { answerSegments, stripEndMarker } from "./answer-integrity";
/**
 * The trainer's question, parsed out of the completed answer (`D112`).
 *
 * ── WHERE THE `ask` LIVES ON THE WIRE ───────────────────────────────────────
 * `apps/ai/src/agents/trainer-agent.ts` appends it AFTER the validated prose,
 * as one marker character and one line of compact JSON, and only then appends
 * the end-of-answer marker — so the `ask` sits INSIDE the text `D77`'s marker
 * certifies. A stream that died before the end marker also died before or
 * inside the `ask`, and `assessAnswer` already discards that answer whole;
 * nothing is lost, because the ask is derived from STORED progress and the
 * next turn recomputes the same question.
 *
 * This parser is the sibling of `citations.ts`: it reads what the SERVER
 * attached, found the same way the sources block is found — by a structural
 * anchor in the completed text, never by asking the model to describe it.
 *
 * ── WHY A FORGED `ask` CANNOT REACH HERE ────────────────────────────────────
 * The server scrubs U+241F from the model's own prose before appending its
 * block, exactly as `withEndMarker` scrubs U+241E — so a model (or a user who
 * asks the model to type the character) cannot attach a question the state
 * machine never chose. The LAST marker in the text is therefore the server's,
 * and it is also the only one.
 *
 * ── THE MARKER IS RESTATED, WHICH IS A HAZARD, AND IT IS THE KNOWN ONE ──────
 * `ANSWER_ASK_MARKER` below is the same character `apps/ai` exports, declared
 * twice because that Worker's bundle drags `agents`, `ai` and the AI SDK
 * behind it and cannot be a dependency of a phone — the exact situation
 * `answer-integrity.ts` documents for U+241E. Unlike that one, this copy has a
 * guard: `__tests__/ask.test.ts` reads the Worker's source through `?raw` and
 * asserts the declaration is still there, because drift here is silent in the
 * worst direction — every `ask` parses as `null`, forever, and the onboarding
 * simply never shows a control.
 */
/**
 * U+241F SYMBOL FOR UNIT SEPARATOR — the end marker's (U+241E) sibling. One
 * code point (no chunker can split it), not whitespace (no `trim()` can eat
 * it), not a character a trainer's answer contains.
 */
export const ANSWER_ASK_MARKER = "␟";
/** What kind of control collects the answer (`D112`). */
export const ASK_KINDS = ["choice", "number", "date", "screen"];
/** The two single-task screens a step can open (`wire-contracts` §4). */
export const ASK_SCREENS = ["body-map", "equipment"];
/**
 * The prose without the `ask` block — what the screen shows.
 *
 * Cut from the FIRST marker to the end of the string: the block is the last
 * thing before the end marker, and the scrubbing above means no earlier
 * marker can exist. During streaming this also hides a half-arrived block the
 * instant its marker lands, so no reader ever watches JSON type itself out.
 */
export function stripAskBlock(text) {
    const start = text.indexOf(ANSWER_ASK_MARKER);
    return start < 0 ? text : text.slice(0, start);
}
/**
 * The `ask` out of a COMPLETED answer's raw text, or `null`.
 *
 * `null` is every failure mode at once — no marker, malformed JSON, a shape
 * the vocabulary does not admit — and deliberately so: a chat with no control
 * still works as a chat, and the next turn recomputes the question from
 * stored progress. Throwing here would turn one bad byte into a dead screen.
 */
export function parseAsk(raw) {
    const start = raw.lastIndexOf(ANSWER_ASK_MARKER);
    if (start < 0) {
        return null;
    }
    const tail = stripEndMarker(raw.slice(start + 1)).trim();
    let parsed;
    try {
        parsed = JSON.parse(tail);
    }
    catch {
        return null;
    }
    return readAsk(parsed);
}
/**
 * The `Ask` validator, over an already-parsed value.
 *
 * Private again since `T15`. It was exported for `onboarding-session.ts`, so
 * that a question restored from the device was validated by the SAME rules the
 * wire uses; that store is gone with the chat's onboarding (`D126`) and
 * {@link parseAsk} is the only reader left. `intake.ts` builds its asks
 * locally, from `ONBOARDING_CHOICE_VALUES`, and never parses one.
 */
function readAsk(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const record = value;
    const { kind, step } = record;
    if (typeof step !== "string" || step === "") {
        return null;
    }
    if (!ASK_KINDS.includes(kind)) {
        return null;
    }
    const options = readOptions(record.options);
    if (options === undefined) {
        return null;
    }
    if (record.screen !== undefined && !isAskScreen(record.screen)) {
        return null;
    }
    if (!(isOptional(record.multiple, "boolean") &&
        isOptional(record.unit, "string") &&
        isOptional(record.min, "number") &&
        isOptional(record.max, "number") &&
        isOptional(record.stepBy, "number"))) {
        return null;
    }
    return {
        kind: kind,
        max: record.max,
        min: record.min,
        multiple: record.multiple,
        options,
        screen: record.screen,
        step,
        stepBy: record.stepBy,
        unit: record.unit,
    };
}
function isOptional(value, type) {
    return value === undefined || typeof value === type;
}
function isAskScreen(value) {
    return ASK_SCREENS.includes(value);
}
/** `undefined` = invalid (refuse the whole ask); absent options are legal. */
function readOptions(value) {
    if (value === undefined) {
        return [];
    }
    if (!Array.isArray(value)) {
        return;
    }
    const options = [];
    for (const entry of value) {
        if (typeof entry !== "object" || entry === null) {
            return;
        }
        const { labelKey, value: optionValue } = entry;
        if (typeof labelKey !== "string" ||
            labelKey === "" ||
            typeof optionValue !== "string" ||
            optionValue === "") {
            return;
        }
        options.push({ labelKey, value: optionValue });
    }
    return options;
}
// ── The local half of the script (`D113` · `D114`) ──────────────────────────
//
// The agent computes its `ask` from STORED progress, and stored progress lags
// the conversation on purpose: the eight config-backed steps complete
// TOGETHER when the `training_config` row lands (`script.ts`, consequence 1),
// and the experience follow-ups are not steps at all. So for the whole middle
// of Session 1 the server keeps attaching the same stale question ("goal"),
// and the APP walks the script forward from what the person just answered —
// `answer.ts` is the walker, and these builders are its vocabulary. The
// stored progress catches up at the next write, at which point the server's
// own `ask` takes over again.
/**
 * The intake's vocabulary — the three experience follow-ups
 * (`EXPERIENCE_FOLLOW_UP_STEPS`), the home equipment chips
 * (`HOME_EQUIPMENT_OPTIONS`) and every choice step's option values — moved to
 * `@repo/contract/training` (plan D52), because the server's trainer write
 * tool must map an answer exactly as the stepper does. The contract is the
 * source; the option table is re-exported here for this module's label test,
 * and nothing is restated.
 */
export { ONBOARDING_CHOICE_VALUES } from "@repo/contract/training";
/**
 * Every step that renders a control, local follow-ups included — each needs
 * an `onboarding.<step>.question` line, because the model is FORBIDDEN to ask
 * (`prompt.ts`: "The APP asks the questions and draws the controls").
 */
export const QUESTION_STEPS = [
    "date_of_birth",
    "sex",
    "height",
    "weight",
    "goal",
    "secondary_goal",
    "days_per_week",
    "days_fixed",
    "fixed_weekdays",
    "session_minutes",
    "session_hard_limit",
    "parq",
    "current_pain",
    "equipment_place",
    "equipment",
    "experience",
    ...EXPERIENCE_FOLLOW_UP_STEPS,
];
// ── Input validation for the two typed controls ─────────────────────────────
/**
 * A typed number against the ask's bounds, or `null`.
 *
 * Accepts a comma as the decimal separator — the keyboard pt-BR readers get —
 * and a decimal at all only when `stepBy` says the step is fractional, so a
 * height of "175.5" is refused rather than silently truncated.
 */
const PLAIN_NUMBER = /^\d+(\.\d+)?$/;
const ONE_DECIMAL_NUMBER = /^\d+(\.\d)?$/;
const ONE_OR_TWO_DIGITS = /^\d{1,2}$/;
const FOUR_DIGITS = /^\d{4}$/;
export function parseNumberAnswer(text, ask) {
    const normalized = text.trim().replace(",", ".");
    if (!PLAIN_NUMBER.test(normalized)) {
        return null;
    }
    const value = Number(normalized);
    const wholeStep = ask.stepBy === undefined || Number.isInteger(ask.stepBy);
    if (wholeStep && !Number.isInteger(value)) {
        return null;
    }
    // One decimal is all any fractional step in the script carries (weight,
    // 0.1); more precision than the step offers is a typo, not a measurement.
    if (!(wholeStep || ONE_DECIMAL_NUMBER.test(normalized))) {
        return null;
    }
    if (ask.min !== undefined && value < ask.min) {
        return null;
    }
    if (ask.max !== undefined && value > ask.max) {
        return null;
    }
    return value;
}
/**
 * Three typed fields into the wire's `YYYY-MM-DD`, or `null`.
 *
 * Round-tripped through `Date.UTC` so "31/02/1990" is refused instead of
 * becoming the 2nd of March, and bounded to a birth date a living person can
 * have: not in the future, not more than 120 years back.
 */
export function calendarDayFromParts(parts, today) {
    if (!(ONE_OR_TWO_DIGITS.test(parts.day.trim()) &&
        ONE_OR_TWO_DIGITS.test(parts.month.trim()) &&
        FOUR_DIGITS.test(parts.year.trim()))) {
        return null;
    }
    const day = Number(parts.day);
    const month = Number(parts.month);
    const year = Number(parts.year);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day) {
        return null;
    }
    if (date.getTime() > today.getTime()) {
        return null;
    }
    if (year < today.getUTCFullYear() - 120) {
        return null;
    }
    const pad = (value) => String(value).padStart(2, "0");
    return `${year}-${pad(month)}-${pad(day)}`;
}
/**
 * The display text of each answer a raw message holds (`D191`), for
 * `ChatMessage.segments` — or `undefined` when it holds one, which `text`
 * already is.
 */
export function displaySegments(raw) {
    const segments = answerSegments(raw)
        .map((segment) => stripAskBlock(segment).trim())
        .filter((segment) => segment !== "");
    return segments.length > 1 ? segments : undefined;
}
//# sourceMappingURL=ask.js.map